import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Search, Send, Eye, X, RefreshCw, Loader2,
  AlertTriangle, CheckCircle, XCircle, Clock, Filter
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import AdminLayout from '../../components/admin/AdminLayout';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import {
  getAdminEmailLogs, getAdminEmailTemplates, sendAdminEmail
} from '../../api/client';

const templateLabels: Record<string, string> = {
  email_otp: 'OTP / Verification',
  welcome_email: 'Welcome Email',
  enrollment_confirmation: 'Enrollment',
  payment_receipt: 'Payment',
  certificate_ready: 'Certificate',
  admin_message: 'Admin Message',
  verification_approved: 'Approved',
  verification_rejected: 'Rejected',
  account_suspended: 'Suspended',
  account_unsuspended: 'Unsuspended',
  abandoned_cart: 'Abandoned Cart',
  lead_nurture: 'Lead Nurture',
};

const statusColors: Record<string, string> = {
  sent: 'bg-green-100 text-green-700',
  delivered: 'bg-blue-100 text-blue-700',
  opened: 'bg-purple-100 text-purple-700',
  failed: 'bg-red-100 text-red-700',
  bounced: 'bg-orange-100 text-orange-700',
};

function EmailPreviewModal({ email, onClose }: { email: any; onClose: () => void }) {
  if (!email) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye size={18} className="text-slate-500" />
            <h3 className="text-sm font-bold text-slate-900">Email Preview</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="p-4 border-b bg-slate-50 space-y-1 text-xs">
          <div><b>To:</b> {email.to_email || email.recipient}</div>
          <div><b>Subject:</b> {email.subject}</div>
          <div><b>Template:</b> {templateLabels[email.template_name] || email.template_name || 'N/A'}</div>
          <div><b>Status:</b> <Badge className={statusColors[email.status] || ''}>{email.status}</Badge></div>
          <div><b>Sent:</b> {email.created_at ? new Date(email.created_at).toLocaleString() : 'N/A'}</div>
        </div>
        <div className="flex-1 overflow-y-auto p-0">
          <iframe srcDoc={email.body_html || email.body || '<p class="p-4 text-center text-slate-400">No content</p>'} className="w-full" style={{ minHeight: 400, border: 'none' }} />
        </div>
      </div>
    </div>
  );
}

function SendEmailModal({ onClose, onSent }: { onClose: () => void; onSent: () => void }) {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email || !subject || !message) { toast.show('Email, subject and message required', 'error'); return; }
    setSending(true);
    try {
      const r: any = await sendAdminEmail(email, name, subject, message);
      if (r.success) { toast.show('Email sent', 'success'); onSent(); onClose(); }
      else toast.show(r.error || 'Failed', 'error');
    } catch (e: any) { toast.show(e.message, 'error'); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Send size={16} /> Send Branded Email</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X size={18} /></button>
        </div>
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Recipient Email *" className="w-full text-sm border rounded-lg px-3 py-2" />
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Recipient Name" className="w-full text-sm border rounded-lg px-3 py-2" />
        <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject *" className="w-full text-sm border rounded-lg px-3 py-2" />
        <textarea value={message} onChange={e => setMessage(e.target.value)} rows={5} placeholder="Message *" className="w-full text-sm border rounded-lg px-3 py-2" />
        <p className="text-xs text-slate-500">This sends a branded HTML email via Brevo with EcoleMinds styling.</p>
        <div className="flex gap-2">
          <Button onClick={handleSend} isLoading={sending} disabled={!email || !subject || !message}>Send Email</Button>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </Card>
    </div>
  );
}

export default function EmailManager() {
  const toast = useToast();
  const [logs, setLogs] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [previewEmail, setPreviewEmail] = useState<any>(null);
  const [showSend, setShowSend] = useState(false);

  const load = useCallback(async (p?: number) => {
    setLoading(true);
    try {
      const r: any = await getAdminEmailLogs(p || page, search || undefined, templateFilter || undefined, statusFilter || undefined);
      setLogs(r.data?.logs || []);
      setTotal(r.data?.total || 0);
      setPages(r.data?.pages || 1);
    } catch (e: any) { toast.show(e.message, 'error'); }
    finally { setLoading(false); }
  }, [page, search, templateFilter, statusFilter]);

  const loadTemplates = async () => {
    try { const r: any = await getAdminEmailTemplates(); setTemplates(r.data?.templates || []); } catch {}
  };

  useEffect(() => { load(1); loadTemplates(); }, []);
  useEffect(() => { const t = setTimeout(() => load(1), 300); return () => clearTimeout(t); }, [search, templateFilter, statusFilter]);

  const handleRefresh = () => load(page);

  return (
    <AdminLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Mail size={22} className="text-amber-600" /> Email Manager</h1>
            <p className="text-xs text-slate-500 mt-0.5">View email logs, templates, and send branded emails</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={handleRefresh} leftIcon={<RefreshCw size={12} />}>Refresh</Button>
            <Button size="sm" onClick={() => setShowSend(true)} leftIcon={<Send size={12} />}>Send Email</Button>
          </div>
        </div>

        {/* Template stats */}
        {templates.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {templates.map((t: any) => (
              <button key={t.template_name} onClick={() => setTemplateFilter(t.template_name === templateFilter ? '' : t.template_name)} className={`text-left p-3 rounded-xl border transition ${templateFilter === t.template_name ? 'border-amber-300 bg-amber-50' : 'bg-white hover:border-slate-300'}`}>
                <div className="text-xs text-slate-500">{templateLabels[t.template_name] || t.template_name}</div>
                <div className="text-lg font-bold text-slate-900">{t.total_count}</div>
                <div className="flex items-center gap-2 text-xs mt-1">
                  <span className="text-green-600">{t.sent_count} sent</span>
                  <span className="text-red-500">{t.failed_count} failed</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Filters */}
        <Card className="p-3 flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search email / subject..." className="pl-8 pr-3 py-1.5 text-xs border rounded-lg w-48" />
          </div>
          <select value={templateFilter} onChange={e => setTemplateFilter(e.target.value)} className="text-xs border rounded-lg px-2 py-1.5">
            <option value="">All Templates</option>
            {templates.map((t: any) => <option key={t.template_name} value={t.template_name}>{templateLabels[t.template_name] || t.template_name}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="text-xs border rounded-lg px-2 py-1.5">
            <option value="">All Status</option>
            <option value="sent">Sent</option>
            <option value="delivered">Delivered</option>
            <option value="opened">Opened</option>
            <option value="failed">Failed</option>
            <option value="bounced">Bounced</option>
          </select>
          <button onClick={() => { setSearch(''); setTemplateFilter(''); setStatusFilter(''); }} className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded border">
            <X size={12} className="inline mr-1" />Clear
          </button>
        </Card>

        {/* Logs */}
        {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-slate-400" size={32} /></div> :
        <div className="space-y-2">
          {logs.map((log: any) => (
            <Card key={log.id} className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    log.status === 'sent' ? 'bg-green-100' : log.status === 'failed' ? 'bg-red-100' : log.status === 'bounced' ? 'bg-orange-100' : 'bg-blue-100'
                  }`}>
                    {log.status === 'sent' ? <CheckCircle size={14} className="text-green-600" /> :
                     log.status === 'failed' ? <XCircle size={14} className="text-red-600" /> :
                     log.status === 'bounced' ? <AlertTriangle size={14} className="text-orange-600" /> :
                     <Mail size={14} className="text-blue-600" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-800 truncate">{log.to_email || log.recipient}</span>
                      <Badge className={statusColors[log.status] || ''}>{log.status}</Badge>
                      <Badge>{templateLabels[log.template_name] || log.template_name || 'N/A'}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 truncate">{log.subject}</div>
                    <div className="text-xs text-slate-400">{log.created_at ? new Date(log.created_at).toLocaleString() : ''}</div>
                  </div>
                </div>
                <button onClick={() => setPreviewEmail(log)} className="p-2 rounded-lg hover:bg-slate-100 flex-shrink-0"><Eye size={16} className="text-slate-500" /></button>
              </div>
            </Card>
          ))}
          {logs.length === 0 && <p className="text-center text-slate-400 text-sm py-8">No emails sent yet.</p>}
        </div>}

        {pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button disabled={page <= 1} onClick={() => { setPage(page - 1); load(page - 1); }} className="px-3 py-1 text-xs rounded border disabled:opacity-50">Prev</button>
            <span className="text-xs text-slate-500">{page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => { setPage(page + 1); load(page + 1); }} className="px-3 py-1 text-xs rounded border disabled:opacity-50">Next</button>
          </div>
        )}
      </div>

      {previewEmail && <EmailPreviewModal email={previewEmail} onClose={() => setPreviewEmail(null)} />}
      {showSend && <SendEmailModal onClose={() => setShowSend(false)} onSent={handleRefresh} />}
    </AdminLayout>
  );
}
